import { Box, Skeleton, useTheme, Switch, Stack } from '@mui/material';
import { useCallback, useMemo, useRef, useState, useEffect } from 'react';
import { scaleTime } from 'd3-scale';
import { debounce } from 'lodash';
import { trackEvent } from '@surfline/web-common';

import DayNightShading from 'components/DayNightShading';
import ErrorBoundary from 'components/ErrorBoundary';
import GraphLineIcon from 'components/Icons/GraphLineIcon';
import GraphLiveWindIcon from 'components/Icons/GraphLiveWindIcon';
import LiveWindBarGraph from 'components/LiveWindBarGraph';
import LiveWindLineGraph from 'components/LiveWindLineGraph';
import LiveWindModalError from 'components/LiveWindModal/LiveWindModalError';
import WindStatsTooltip from 'components/LiveWindModal/WindStatsTooltip';
import TooltipProvider from 'components/TooltipProvider';
import WindForecastData from 'components/WindForecastData';
import { getChartSunlightTimes } from 'selectors/graphs';
import { getSpotDetails, getSpotReportAssociated } from 'selectors/spot';
import { useAppSelector } from 'stores/hooks';
import type { LiveWindResponse, LiveWindPoint } from 'types/liveWind';
import type { SpotDetails, SpotReportAssociated } from 'types/spot';
import type { SunlightTimes } from 'types/sunlightTimes';
import type { WindTimestep } from 'types/wind';
import type { SimpleRatingPoint } from 'types/ratingForecast';
import { convertToSeconds, oneDay } from 'utils/time';
import midnightOfDate from 'utils/midnightOfDate';
import { useLiveWindModalStationIsDataLagging } from 'hooks/liveWind/useLiveWindModal';
import useFetchRatings from './hooks/useFetchRatings';
import TimeLine from './TimeLine';
import useStoredGraphToggle, { LINE } from './hooks/useStoredGraphToggle';
import useFetchForecastedWind from './hooks/useFetchForecastedWind';

import styles from './LiveWindDataVisualizer.module.scss';

export const isDataLagging = (
  liveWind: any,
  nowKey: number,
  dataKey: number,
  lastObservedTimestamp: number | undefined,
) => {
  if (
    dataKey > 2 &&
    nowKey - dataKey >= 0 &&
    nowKey - dataKey <= 3 &&
    liveWind?.data?.[nowKey].sensorWind === false &&
    lastObservedTimestamp !== undefined &&
    lastObservedTimestamp > 0
  ) {
    if (
      nowKey === dataKey &&
      (liveWind?.data?.[nowKey - 1]?.sensorWind === true ||
        liveWind?.data?.[nowKey - 2]?.sensorWind === true ||
        liveWind?.data?.[nowKey - 3]?.sensorWind === true ||
        liveWind?.data?.[nowKey - 4]?.sensorWind === true)
    ) {
      return true;
    }
    if (
      nowKey - dataKey === 1 &&
      (liveWind?.data?.[nowKey - 2].sensorWind === true ||
        liveWind?.data?.[nowKey - 3].sensorWind === true ||
        liveWind?.data?.[nowKey - 4].sensorWind === true)
    ) {
      return true;
    }
    if (
      nowKey - dataKey === 2 &&
      liveWind?.data?.[nowKey - 2].sensorWind === false &&
      (liveWind?.data?.[nowKey - 3].sensorWind === true ||
        liveWind?.data?.[nowKey - 4].sensorWind === true)
    ) {
      return true;
    }
    if (
      nowKey - dataKey === 3 &&
      liveWind?.data?.[nowKey - 1].sensorWind === false &&
      liveWind?.data?.[nowKey - 2].sensorWind === false &&
      liveWind?.data?.[nowKey - 3].sensorWind === false &&
      liveWind?.data?.[nowKey - 4].sensorWind === true
    ) {
      return true;
    }
  }
  return false;
};
interface Props {
  liveWind?: LiveWindResponse;
  isLoading?: boolean;
  setWindDirection: React.Dispatch<React.SetStateAction<number>>;
}

const LiveWindDataVisualizerContent: React.FC<Props> = ({
  liveWind,
  isLoading = false,
  setWindDirection,
}) => {
  const theme = useTheme();
  const canvasRef = useRef<HTMLDivElement | null>(null);
  const graphScrubbedTrackedRef = useRef(false);

  const { graph, toggleGraph } = useStoredGraphToggle();
  const spot: SpotDetails = useAppSelector(getSpotDetails);
  const { data: ratingsData, loading: ratingsLoading } = useFetchRatings(spot?._id);
  const reportAssociated: SpotReportAssociated = useAppSelector(getSpotReportAssociated);

  const [, setIsDataLagging] = useLiveWindModalStationIsDataLagging();

  const {
    data: windForecastData,
    error: errorFetchingWindForecast,
    loading: isWindForecastLoading,
  } = useFetchForecastedWind(spot?._id, reportAssociated.units, graph !== LINE);
  const sunlightTimes: SunlightTimes = useAppSelector(getChartSunlightTimes);

  const hourlyRatings = useMemo<SimpleRatingPoint[]>(() => {
    if (!ratingsData?.data?.rating) return [];

    // Get the data and transform it
    return ratingsData.data.rating.map((item) => ({
      timestamp: item.timestamp,
      utcOffset: item.utcOffset || 0,
      rating: item.rating.key,
    }));
  }, [ratingsData]);

  const liveWindDataLoading = !liveWind || !liveWind?.data?.length;
  const isDataLoading =
    isLoading ||
    liveWindDataLoading ||
    isWindForecastLoading ||
    !spot ||
    !reportAssociated ||
    ratingsLoading;

  const midnightToday = useMemo(
    () => midnightOfDate(reportAssociated?.utcOffset),
    [reportAssociated?.utcOffset],
  );
  const startTimestamp = midnightToday;
  const endTimestamp = midnightToday + convertToSeconds(oneDay);

  useEffect(() => {
    // The error boundary wrapping this will catch the error, send to NewRelic and display the error state
    if (errorFetchingWindForecast) throw errorFetchingWindForecast;
  }, [errorFetchingWindForecast]);

  const initialXPercentage = useMemo(() => {
    const currentUnixTimestamp = Math.floor(+new Date() / 1000);
    return (currentUnixTimestamp - startTimestamp) / (endTimestamp - startTimestamp);
  }, [endTimestamp, startTimestamp]);

  const liveWindData = useMemo(
    () => (liveWind && liveWind?.data?.length ? liveWind.data : []),
    [liveWind],
  );

  const [isMounted, setMounted] = useState(false);
  useEffect(() => {
    setMounted(true);
  }, []);

  const xScale = useMemo(() => {
    if (!!liveWindData.length && !!canvasRef?.current && isMounted) {
      const first = liveWindData[0];
      const last = liveWindData[liveWindData.length - 1];
      return scaleTime()
        .range([0, canvasRef.current.offsetWidth - 2])
        .domain([first.timestamp, last.timestamp]);
    }

    return null;
  }, [liveWindData, canvasRef, isMounted]);

  useEffect(() => {
    // reset the tracking flag each time the user changes graph types
    graphScrubbedTrackedRef.current = false;
  }, [graph]);

  const trackGraphScrubbed = useMemo(
    () =>
      debounce(() => {
        if (!graphScrubbedTrackedRef.current) {
          graphScrubbedTrackedRef.current = true;
          trackEvent('Graph Scrubbed', {
            forecastEnd: 1,
            forecastStart: 1,
            graphType: graph === LINE ? 'live-wind-line' : 'live-wind-bar',
            moduleName: 'live wind',
            pageName: 'Spot Report',
            spotId: spot?._id,
            spotName: spot?.name,
          });
        }
      }, 1000),
    [graph, spot?._id, spot?.name],
  );

  const nowKey = useMemo(
    () => Math.floor((liveWind?.data?.length ?? 0) * initialXPercentage),
    [initialXPercentage, liveWind?.data.length],
  );

  let lastObservedData: LiveWindPoint | null = null;

  const renderTooltip = useCallback(
    (xPercentage: number) => {
      const observedWind =
        liveWind && liveWind?.data?.length > 0
          ? liveWind.data[Math.floor(liveWind.data.length * xPercentage)]
          : null;
      const forecastedWind =
        graph === LINE && windForecastData?.data?.wind && windForecastData?.data?.wind?.length > 0
          ? windForecastData.data.wind[Math.floor(windForecastData.data.wind.length * xPercentage)]
          : undefined;

      const currentUnixTimestamp = Math.floor(+new Date() / 1000);
      const lastObservedTimestamp =
        liveWind && liveWind?.data?.length > 0
          ? Math.max(
              ...liveWind.data.map((liveWindPoint: LiveWindPoint) => {
                if (
                  liveWindPoint.sensorWind === true &&
                  liveWindPoint.timestamp < (observedWind?.timestamp || currentUnixTimestamp)
                ) {
                  return liveWindPoint.timestamp;
                }
                return 0;
              }),
            )
          : undefined;

      if (observedWind) {
        setWindDirection(observedWind.wind.direction);
      }
      // Check if the data is lagging
      const dataKey =
        liveWind?.data && liveWind?.data?.length > 0
          ? Math.floor((liveWind?.data?.length ?? 0) * xPercentage)
          : nowKey;
      const isDataLaggingResult: boolean = isDataLagging(
        liveWind,
        nowKey,
        dataKey,
        lastObservedTimestamp,
      );
      if (isDataLaggingResult) {
        const [lastObservedReading] =
          liveWind?.data.filter((reading) => reading.timestamp === lastObservedTimestamp) || [];
        lastObservedData = lastObservedReading;
        lastObservedData.sensorWind = true;
        setIsDataLagging(true);
      } else {
        setIsDataLagging(false);
        lastObservedData = null;
      }
      return (
        <>
          {!isLoading && xScale && liveWind?.data && (
            <TimeLine
              data={liveWind.data}
              xScale={xScale}
              xPercentage={xPercentage}
              graph={graph as 'LINE' | 'BAR'}
            />
          )}
          <div className={styles.windStatsTooltip}>
            <WindStatsTooltip
              forecastedWind={forecastedWind}
              isLoading={isLoading}
              observedWind={lastObservedData || observedWind!}
              windSpeedUnits={reportAssociated.units.windSpeed}
              lastObservedTimestamp={lastObservedTimestamp}
              utcOffset={reportAssociated.utcOffset}
            />
          </div>
        </>
      );
    },
    [
      liveWind,
      graph,
      windForecastData?.data?.wind,
      isLoading,
      xScale,
      reportAssociated.units.windSpeed,
      reportAssociated.utcOffset,
      setWindDirection,
    ],
  );

  return (
    <Stack
      direction="column"
      justifyContent="center"
      width="100%"
      height="100%"
      data-testid="live-wind-data-visualizer-container"
    >
      <Box className={styles.switchRoot}>
        <Switch
          variant="outlined"
          checked={graph === LINE}
          onChange={toggleGraph}
          color="default"
        />
        <Box className={styles.switchIconLeft}>
          <GraphLineIcon color={theme.palette.text.brandSecondary} />
        </Box>
        <Box className={styles.switchIconRight}>
          <GraphLiveWindIcon />
        </Box>
      </Box>
      <Box className={styles.windStatsContainer} />
      <Box className={styles.graphCanvas} ref={canvasRef}>
        <TooltipProvider
          renderTooltip={renderTooltip}
          onScrub={trackGraphScrubbed}
          cssModules={{ tooltip: styles.tooltip, root: styles.tooltipRoot }}
          forceShow
          currentMarkerXPercentage={initialXPercentage}
        >
          {isDataLoading || !xScale || !canvasRef?.current ? (
            <Skeleton
              variant="rectangular"
              width="100%"
              height={180}
              animation="wave"
              data-testid="live-wind-graph-loading"
            />
          ) : (
            <Stack sx={{ height: 180, width: 1 }} justifyContent="center" alignItems="center">
              <DayNightShading
                columnIndex={0}
                forecastDayIndex={0}
                isPaywalled={false}
                numDays={1}
                sunlightTimes={sunlightTimes}
              />
              {graph === LINE ? (
                <LiveWindLineGraph
                  xScale={xScale}
                  liveWind={liveWindData}
                  ratings={hourlyRatings}
                  forecastedWind={windForecastData?.data?.wind as WindTimestep[]}
                  canvasRef={canvasRef as React.MutableRefObject<HTMLDivElement>}
                  currentMarkerXPercentage={initialXPercentage}
                />
              ) : (
                <LiveWindBarGraph
                  liveWind={liveWind?.data}
                  xScale={xScale}
                  ratings={hourlyRatings}
                  canvasRef={canvasRef as React.MutableRefObject<HTMLDivElement>}
                  currentMarkerXPercentage={initialXPercentage}
                />
              )}
            </Stack>
          )}
        </TooltipProvider>
      </Box>
      <Box className={styles.windForecastDataContainer}>
        {isDataLoading || !canvasRef?.current ? (
          <Skeleton
            variant="rectangular"
            width="100%"
            height={109}
            animation="wave"
            data-testid="live-wind-forecast-data-loading"
          />
        ) : (
          <WindForecastData graphWidth={canvasRef?.current?.offsetWidth} wind={liveWind?.data} />
        )}
      </Box>
    </Stack>
  );
};

export const LiveWindDataVisualizer: React.FC<Props> = ({ ...restProps }) => (
  <ErrorBoundary
    errorBoundaryId="live-wind-data-visualizer"
    render={() => (
      <Box className={styles.errorContainer} data-testid="live-wind-data-visualizer-error">
        <LiveWindModalError message="Something went wrong here." />
      </Box>
    )}
  >
    <LiveWindDataVisualizerContent {...restProps} />
  </ErrorBoundary>
);

export default LiveWindDataVisualizer;
